import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { m as mDiv, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { SinceFoundation } from '../SinceFoundation';

interface HeaderProps {
  onAboutClick?: () => void;
}

export function Header({ onAboutClick }: HeaderProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Real-time clock — skips updates when the tab is hidden to avoid wasted renders
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        setCurrentTime(new Date());
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const clockParts = (() => {
    const day = currentTime.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
    const month = currentTime.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
    const dayNum = currentTime.getDate();
    const h = currentTime.getHours().toString().padStart(2, '0');
    const min = currentTime.getMinutes().toString().padStart(2, '0');
    const s = currentTime.getSeconds().toString().padStart(2, '0');
    return { day, month, dayNum, h, min, s };
  })();

  const navItems = [
    { label: 'WORK', href: '/', isLink: true },
    { label: 'LAB', href: '/lab', isLink: true },
    { label: 'ABOUT', href: '#', isLink: false },
  ];

  const isNavActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/work');
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--color-background)] border-b border-[var(--color-rule)]">
        <div className="px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="relative z-10 font-en font-bold text-lg md:text-xl uppercase tracking-wide text-white hover:opacity-60 transition-opacity"
          >
            LIGHTIVE
          </Link>

          {/* Center: Clock + Since (Desktop) */}
          <div className="hidden lg:flex items-center font-en text-[11px] text-white/60 tracking-wider font-bold uppercase tabular-nums">
            <span>{clockParts.day}, {clockParts.dayNum} {clockParts.month} {clockParts.h}:{clockParts.min}:</span>
            <mDiv.span
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
              className="text-white/60"
            >
              {clockParts.s}
            </mDiv.span>
            <span className="mx-3 text-white/15">|</span>
            <SinceFoundation variant="live" className="text-white/40" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => {
              if (!item.isLink) {
                return (
                  <button
                    key={item.label}
                    onClick={onAboutClick}
                    className="font-en font-medium text-sm uppercase tracking-widest text-white/40 hover:text-white hover:[box-shadow:inset_0_-1px_0_currentColor] transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={clsx(
                    'font-en font-medium text-sm uppercase tracking-widest transition-colors duration-200',
                    isNavActive(item.href)
                      ? 'text-white [box-shadow:inset_0_-1px_0_currentColor]'
                      : 'text-white/40 hover:text-white hover:[box-shadow:inset_0_-1px_0_currentColor]'
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="relative z-10 md:hidden p-3 -mr-3 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <mDiv.span
                animate={{
                  rotate: isMobileMenuOpen ? 45 : 0,
                  y: isMobileMenuOpen ? 8 : 0,
                }}
                className="block w-full h-0.5 bg-white origin-left"
              />
              <mDiv.span
                animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
                className="block w-full h-0.5 bg-white"
              />
              <mDiv.span
                animate={{
                  rotate: isMobileMenuOpen ? -45 : 0,
                  y: isMobileMenuOpen ? -8 : 0,
                }}
                className="block w-full h-0.5 bg-white origin-left"
              />
            </div>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <mDiv.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Backdrop */}
            <div
              role="button"
              tabIndex={0}
              className="absolute inset-0 bg-black/95 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsMobileMenuOpen(false); } }}
              aria-label="Close menu"
            />

            {/* Menu Panel */}
            <mDiv.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
              className="absolute top-0 right-0 bottom-0 w-72 bg-black pt-24 px-8"
            >
              <div className="flex flex-col">
                {navItems.map((item, index) => {
                  const dividerClass = index < navItems.length - 1 ? 'border-b border-[var(--color-rule)]' : '';

                  if (!item.isLink) {
                    return (
                      <button
                        key={item.label}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          onAboutClick?.();
                        }}
                        className={clsx(
                          'block font-en font-bold text-4xl uppercase tracking-tight text-white/40 text-left py-4 hover:text-white transition-opacity',
                          dividerClass
                        )}
                      >
                        {item.label}
                      </button>
                    );
                  }

                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      className={clsx(
                        'block font-en font-bold text-4xl uppercase tracking-tight py-4 transition-opacity duration-200',
                        isNavActive(item.href) ? 'text-white' : 'text-white/40',
                        dividerClass
                      )}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>

              {/* Mobile Clock */}
              <div className="mt-12 pt-8 border-t border-[var(--color-rule)]">
                <div className="font-en text-xs text-white/60 uppercase tracking-wider font-bold tabular-nums space-y-2">
                  <p className="flex items-center">
                    <span>{clockParts.day}, {clockParts.dayNum} {clockParts.month} {clockParts.h}:{clockParts.min}:</span>
                    <mDiv.span
                      animate={{ opacity: [1, 0.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                      className="text-white/60"
                    >
                      {clockParts.s}
                    </mDiv.span>
                  </p>
                  <SinceFoundation variant="live" className="text-white/30" />
                </div>
              </div>
            </mDiv.nav>
          </mDiv.div>
        )}
      </AnimatePresence>
    </>
  );
}
